/*
 * Points every link to the corporate customisation page at the right one for the
 * visitor: the US page for US visitors, the UK page for everyone else (UK/ROW).
 *
 * Menus and the splash screen are already localised in Liquid (see the
 * corporate-customisation-url snippet), but links added in page content are
 * hard-coded, and on a first visit `localization.country` is the store default
 * until the visitor picks a country in the footer. So:
 *   1. If the visitor picked a country in the footer, that wins.
 *   2. Otherwise, if Shopify's localisation already says US, use US.
 *   3. Otherwise, detect the visitor's country from their IP via Shopify's
 *      browsing_context_suggestions endpoint (cached for the session).
 *
 * Exposes `window.corporateCustomisation.ready`, a promise resolving to the URL,
 * so the splash screen can wait for it before redirecting.
 */
(function () {
  'use strict';

  var US_PATH = '/pages/corporate-customisation';
  var UK_PATH = '/pages/corporate-customisation-uk';
  var CHOICE_KEY = 'corp-customisation-country-choice';
  var DETECTED_KEY = 'corp-customisation-detected-country';

  var config = window.corporateCustomisation || {};
  var currentCountry = (config.country || '').toUpperCase();

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function rootPrefix() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '');
  }

  function urlFor(country) {
    return rootPrefix() + (country === 'US' ? US_PATH : UK_PATH);
  }

  function isCustomisationUrl(href) {
    var url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return false;
    }
    if (url.origin !== window.location.origin) return false;
    var path = url.pathname.replace(/\/$/, '');
    return endsWith(path, US_PATH) || endsWith(path, UK_PATH);
  }

  function endsWith(value, suffix) {
    return value.slice(-suffix.length) === suffix;
  }

  function rewriteLinks(target) {
    document.querySelectorAll('a[href*="/pages/corporate-customisation"]').forEach(function (link) {
      if (isCustomisationUrl(link.getAttribute('href'))) link.setAttribute('href', target);
    });
    document.querySelectorAll('[data-splash-url]').forEach(function (el) {
      if (isCustomisationUrl(el.getAttribute('data-splash-url'))) el.setAttribute('data-splash-url', target);
    });
  }

  function detectCountry() {
    var cached = storageGet(DETECTED_KEY);
    if (cached !== null) return Promise.resolve(cached);
    if (!window.fetch) return Promise.resolve('');

    return fetch('/browsing_context_suggestions.json?country[enabled]=true', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        return response.ok ? response.json() : {};
      })
      .then(function (data) {
        var detected = data && data.detected_values && data.detected_values.country;
        var code = detected && detected.handle ? String(detected.handle).toUpperCase() : '';
        storageSet(DETECTED_KEY, code);
        return code;
      })
      .catch(function () {
        return '';
      });
  }

  function resolveCountry() {
    var choice = storageGet(CHOICE_KEY);
    if (choice) return Promise.resolve(choice.toUpperCase());
    if (currentCountry === 'US') return Promise.resolve('US');
    return detectCountry().then(function (detected) {
      return detected || currentCountry;
    });
  }

  // Remember an explicit choice from the footer (or header) country selector.
  // localization-form.js submits with form.submit(), which fires no submit event,
  // so listen for the click on the country option instead.
  document.addEventListener(
    'click',
    function (event) {
      var item = event.target.closest && event.target.closest('localization-form a[data-value]');
      if (!item) return;
      var form = item.closest('localization-form').querySelector('form');
      if (form && form.querySelector('input[name="country_code"]')) {
        storageSet(CHOICE_KEY, item.getAttribute('data-value').toUpperCase());
      }
    },
    true
  );

  var ready = resolveCountry().then(function (country) {
    var target = urlFor(country);
    function apply() {
      rewriteLinks(target);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', apply);
    } else {
      apply();
    }
    return target;
  });

  config.ready = ready;
  window.corporateCustomisation = config;
})();
